<template>
	<model-viewer
		:src="$store.state.activeModel"
		environment-image="neutral"
		camera-orbit="45deg 65.77deg 45%"
		min-camera-orbit="auto 0deg 180m"
		max-camera-orbit="auto 94deg 380m"
		animation-crossfade-duration="500"
		animation-control-mode="manual"
		interaction-prompt-style="basic"
		:interaction-prompt="modelPrompt"
		camera-controls
		:class="{ 'hotspots-visible': activePrimaryNavigation === 'keyFeatures' && activeSecondaryNavigation }"
		@progress="updateProgress"
		@load="onModelLoad"
		@camera-change="onCameraChange"
		v-html="$store.state.productConfig.hotspots"
	/>

	<div
		v-if="$store.state.debug"
		class="debug-info"
	>
		<div>
			Orbit: {{ cameraOrbit }}<br>
			Target: {{ cameraTarget }}
		</div>
		<div>
			Active animations:<br>
			<span v-for="(a, i) in $store.state.activeAnimations" :key="i">
				{{ a }}<br>
			</span>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import 'model-viewer'

const easeInOutCubic = (x, t, b, c, d) => {
	if ((t/=d/2) < 1) return c/2*t*t*t + b;
	return c/2*((t-=2)*t*t + 2) + b;
}

export default {
	name: 'ModelView',
	props: {
		playing: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			modelPrompt: 'auto',
			/** @type {?HTMLElement} */
			modelViewer: null,
			/** @type {[Node]} */
			hotspots: [],
			/** @type {Object.<string, THREE.AnimationAction>} */
			modelActions: {},
			/** @type {number} */
			animationsInProgress: 0,

			/** @type {?string} */
			lastRequestedAnimation: null,
			/** @type {?string} */
			lastPlayedName: null,
			/** @type {?THREE.AnimationAction} */
			lastPlayedAction: null,
			/** @type {?string} */
			activePresetAction: null,
			/** @type {?string} */
			activePreset: null,

			/** @type {Object.<string, Texture>} */
			extraTextures: {},

			initialCameraOrbit: '',
			cameraOrbit: '',
			cameraTarget: ''
		}
	},
	computed: mapState({
		activePrimaryNavigation: state => state.navigation.activePrimary,
		activeSecondaryNavigation: state => state.navigation.activeSecondary,
		activeSubExplanation: state => state.explanationPaneSub,
		explanationPaneOpen: state => state.explanationPaneOpen,
		animationsList: state => state.productConfig.animations,
		modelLoadingProgress: state => state.modelLoadingProgress,
		productConfig: state => state.productConfig,
		transferModeOn: state => state.navigation.transferModeOn,
		siderailsCheck: state => state.navigation.siderailsCheck
	}),
	mounted() {
		this.$bus.on('requestModelAnimation', async ({ name }) => {
			console.log('Requested: ' + name)
			if ( this.animationsInProgress ) return

			const isPreset = this.animationsList[name]?.preset

			if (isPreset) {
				if (this.activePreset === name) {
					// reset when same preset position is clicked again
					this.resetModel()
				} else {
					this.positionHandlers().preset(name)
				}

			} else if (this.positionHandlers()[name]) {
				this.positionHandlers()[name]()
			}

			this.lastRequestedAnimation = name
		})

		this.$bus.on('requestModelAnimationsReset', ({ exclude = null } = {}) => {
			this.resetModel(exclude)
		})

		this.$bus.on('requestModelPartsVisibility', nodes => {
			nodes.forEach(node => { this.setModelNodeVisibility(node.name, node.visible) })
		})

		this.$bus.on('requestModelPartsTexture', nodes => {
			nodes.forEach(node => { this.setModelNodeTexture(node.name, node.texture) })
		})

		this.$bus.on('requestCameraPosition', cameraPosition => {
			this.setCameraPosition(cameraPosition)
		})

		// window.modelVM = this // TODO: for debugging only, remove when going into production
	},
	methods: {
		onModelLoad(e) {
			const viewer = e.target
			const actions = {}

			// add all available animations to mixer and set reference for easy access
			for (const [name, animation] of Object.entries(this.animationsList)) {
				const animationTrack = viewer.scene.animationsByName.get(animation.track)

				if (animationTrack) {
					const action = viewer.animationMixer.clipAction(animationTrack)

					action.setLoop(viewer.availableLoopModes.once)
					action.clampWhenFinished = true
					action.isReversed = animation.reversed

					actions[name] = action
				}
			}

			// set up hotspots (positioning is done by modelviewer)
			viewer.querySelectorAll('button').forEach(hotspot => {
				hotspot.setAttribute('disabled', '')
				hotspot.addEventListener('click', () => this.onHotspotClick(hotspot))
				this.hotspots.push(hotspot)
			})

			this.modelActions = actions
			this.modelViewer = viewer
			this.initialCameraOrbit = viewer.cameraOrbit

			window.modelViewer = viewer
			window.modelActions = actions

			this.setModelNodeVisibility('nyPIV', false)
			this.setModelNodeVisibility('bluePIV', false)
			this.setModelNodeVisibility('meteorPIV', false)
			this.$bus.emit('selectModification', 'modifications.siderails', 'modifications.siderails-standard')
		},

		isAnimationActive(name) {
			const isActive = action => action.isReversed && action.timeScale === -1 ? action.time === 0 : action.time > 0

			if (typeof name === 'object') {
				return name.some(it => it in this.modelActions && isActive(this.modelActions[it]))
			} else {
				return name in this.modelActions && isActive(this.modelActions[name])
			}
		},

		async animateSubsequentActions(actionNames = [], speed = 1, reverse) {
			for (const [i, actionName] of actionNames.entries()) {
				this.modelActions[actionName].setEffectiveWeight(1)
				await this.playAnimation(actionName, { speed, reverse, fadeIn: false })

				if (i < actionNames.length - 1) { // reset except for the last action
					this.modelActions[actionName].setEffectiveWeight(0)
					this.modelActions[actionName].time = 0
				}
			}
			return Promise.resolve()
		},

		positionHandlers() {
			const manualPositions = [ 'folding_of_foot_siderails', 'folding_of_head_siderails', 'mobilift' ]
			const motorizedPositions = Object.keys(this.modelActions).filter(it => !manualPositions.includes(it))

			const trendelenburgPositions = [ 'trendelenburg', 'cardiac_to_trendelenburg', 'mid_to_trendelenburg', 'min_to_trendelenburg', 'trendelenburg_backrest_adjustment_30', 'trendelenburg_backrest_adjustment_45', 'trendelenburg_backrest_adjustment_70' ]
			const reverseTrendelenburgPositions = [ 'reversetrendelenburg', 'mid_to_reversetrendelenburg', 'min_to_reversetrendelenburg', 'trendelenburg_to_reverse_trendelenburg', 'reversetrendelenburg_backrest_adjustment_30', 'reversetrendelenburg_backrest_adjustment_45', 'reversetrendelenburg_backrest_adjustment_70' ]
			const cardiacChairPositions = [ 'cardiac_chair', 'cardiac_chair_position_mobilization' ]
			const mobilizationPositions = [ 'mobilization_position', 'mobilization_position_from_mid', 'mobilization_position_from_min' ]
			const minHeightPositions = [ 'mid_to_min', 'trendelenburg_to_min', 'reversetrendelenburg_to_min' ]

			return {
				'folding_of_foot_siderails': () => {
					const alreadyActive = this.isAnimationActive('folding_of_foot_siderails')
					const mobiliftActive = this.isAnimationActive('mobilift')

					if ( alreadyActive && mobiliftActive ) {
						this.positionHandlers().mobilift() // reset mobilift
					}

					if ( this.activePrimaryNavigation === 'controls' && alreadyActive ) {
						this.playAnimation('folding_of_foot_siderails', { reverse: true, fadeIn: false })
					} else if (!alreadyActive) {
						this.playAnimation('folding_of_foot_siderails')
					}
				},
				'folding_of_head_siderails': () => {
					const reverse = this.isAnimationActive('folding_of_head_siderails') && this.activePrimaryNavigation === 'controls'
					this.playAnimation('folding_of_head_siderails', { reverse, fadeIn: !reverse })
				},
				'mobilift': () => {
					if ( !this.isAnimationActive('folding_of_foot_siderails') ) {
						this.playAnimation('folding_of_foot_siderails') // put foot siderails down
					}

					const lActive = this.isAnimationActive('mobilift')
					const rActive = this.isAnimationActive('mobilift_r')

					this.playAnimation('mobilift', { reverse: lActive, fadeIn: false })
					this.playAnimation('mobilift_r', { reverse: rActive, fadeIn: false })
				},

				'bed_height_adjustment_down': async () => {
					if ( this.isAnimationActive(minHeightPositions) ) return

					if ( this.isAnimationActive('trendelenburg_backrest_adjustment_70') ) {
						this.fastForwardAnimation('backrest_adjustment_up')
						this.fadeAnimations(trendelenburgPositions, 1)

						await this.playAnimation('trendelenburg_to_min', { fadeIn: false })

						this.fadeAnimations('trendelenburg_to_min', 0)
						this.fastForwardAnimation('mid_to_min')
						return
					}
					if ( this.isAnimationActive(trendelenburgPositions) ) {
						this.fadeAnimations(trendelenburgPositions, 0)
						await this.playAnimation('trendelenburg_to_min', { fadeIn: false })
						this.fadeAnimations('trendelenburg_to_min', 0)
						this.fastForwardAnimation('mid_to_min')
						return
					}

					if ( this.isAnimationActive('reversetrendelenburg_backrest_adjustment_70') ) {
						this.fastForwardAnimation('backrest_adjustment_up')
						this.fadeAnimations(reverseTrendelenburgPositions, 1)

						await this.playAnimation('reversetrendelenburg_to_min', { fadeIn: false })

						this.fadeAnimations('reversetrendelenburg_to_min', 0)
						this.fastForwardAnimation('mid_to_min')
						return
					}
					if ( this.isAnimationActive(reverseTrendelenburgPositions) ) {
						this.fadeAnimations(reverseTrendelenburgPositions, 0)
						await this.playAnimation('reversetrendelenburg_to_min', { fadeIn: false })
						this.fadeAnimations('reversetrendelenburg_to_min', 0)
						this.fastForwardAnimation('mid_to_min')
						return
					}

					if ( this.isAnimationActive(['max_to_mid', 'mobilization_position']) ) {
						if (this.isAnimationActive(['lateral_l', 'lateral_r'])) return
						this.fadeAnimations(['max_to_mid'], 2)
						this.playAnimation('mid_to_min')
					} else {
						this.playAnimation('max_to_mid')
					}
				},
				'bed_height_adjustment_up': async () => {
					if ( this.isAnimationActive(trendelenburgPositions) ) {
						if ( this.isAnimationActive('trendelenburg_backrest_adjustment_70') ) {
							this.fastForwardAnimation('backrest_adjustment_up')
						}
						await this.fadeAnimations(trendelenburgPositions, 2)
						return
					}
					if ( this.isAnimationActive(reverseTrendelenburgPositions) ) {
						if ( this.isAnimationActive('reversetrendelenburg_backrest_adjustment_70') ) {
							this.playAnimation('backrest_adjustment_up', { start: 2.7 })
						}
						await this.fadeAnimations(reverseTrendelenburgPositions, 2)
						return
					}

					if ( this.isAnimationActive('max_to_mid') ) {
						this.fadeAnimations(['max_to_mid', 'mid_to_min'], 2)
						return
					}

					if ( this.isAnimationActive(minHeightPositions) ) {
						this.fadeAnimations(this.$deleteFromArray(minHeightPositions, 'mid_to_min'), 0)

						await this.playAnimation('mid_to_min', { reverse: true, fadeIn: false })

						this.fastForwardAnimation('max_to_mid')
						this.fadeAnimations(['mid_to_min'], 1)
					}

					if ( this.isAnimationActive(cardiacChairPositions) ) {
						// await this.playAnimation('autocontour_up', { blendFrom: 'cardiac_chair' })

						this.modelActions['autocontour_up'].play()
						this.modelActions['autocontour_up'].setEffectiveWeight(1)
						this.modelActions['autocontour_up'].time = 2.25
						this.modelActions['autocontour_up'].paused = true
						// this.animateActionWeight('autocontour_up', 0.5, 5, 3)
						this.fadeAnimations(cardiacChairPositions, 4)
					}
				},

				'backrest_adjustment_down': async () => {
					if ( this.isAnimationActive('trendelenburg_backrest_adjustment_70') ) {
						await this.animateSubsequentActions([
							'trendelenburg_backrest_adjustment_70',
							'trendelenburg_backrest_adjustment_45',
							'trendelenburg_backrest_adjustment_30'
						])
						return
					}
					if ( this.isAnimationActive('reversetrendelenburg_backrest_adjustment_70') ) {
						await this.animateSubsequentActions([
							'reversetrendelenburg_backrest_adjustment_70',
							'reversetrendelenburg_backrest_adjustment_45',
							'reversetrendelenburg_backrest_adjustment_30'
						])
						return
					}

					if ( this.isAnimationActive(mobilizationPositions) ) {
						if (!this.isAnimationActive('mid_to_min')) {
							this.fastForwardAnimation('max_to_mid')
						}
						await this.fadeAnimations(mobilizationPositions, 2)
					}

					if ( this.isAnimationActive('backrest_adjustment_up') ) {
						await this.playAnimation('backrest_adjustment_up', { reverse: true, fadeIn: false })
						return
					}
				},
				'backrest_adjustment_up': async () => {
					if ( this.isAnimationActive([ 'backrest_adjustment_up', 'trendelenburg_backrest_adjustment_70', 'reversetrendelenburg_backrest_adjustment_70', 'autocontour_up', 'trendelenburg_autokontura', 'reversetrendelenburg_autokontura', 'cardiac_chair', ...mobilizationPositions ]) ) return

					if ( this.$store.state.activeProduct === 'essenza300' && this.isAnimationActive(trendelenburgPositions) ) {
						await this.fadeAnimations(trendelenburgPositions, 0)
						await this.animateSubsequentActions([
							'trendelenburg_backrest_adjustment_30',
							'trendelenburg_backrest_adjustment_45',
							'trendelenburg_backrest_adjustment_70'
						], 1, true)
						return
					}
					if ( this.$store.state.activeProduct === 'essenza300' && this.isAnimationActive(reverseTrendelenburgPositions) ) {
						await this.fadeAnimations(reverseTrendelenburgPositions, 0)
						await this.animateSubsequentActions([
							'reversetrendelenburg_backrest_adjustment_30',
							'reversetrendelenburg_backrest_adjustment_45',
							'reversetrendelenburg_backrest_adjustment_70'
						], 1, true)
						return
					}

					await this.playAnimation('backrest_adjustment_up')
				},

				'thighrest_adjustment_down': async () => {
					if ( !this.isAnimationActive([ 'nohy_rovina', 'striska' ]) ) return

					if ( this.isAnimationActive('striska') ) {
						if ( this.isAnimationActive('nohy_rovina') ) {
							await this.playAnimation('nohy_rovina', { reverse: true, fadeIn: false, weight: 50 })
							this.fadeAnimations('nohy_rovina', 0)
						} else {
							await this.playAnimation('striska', { reverse: true, fadeIn: false })
						}
						return
					}
				},
				'thighrest_adjustment_up': async () => {
					if ( this.isAnimationActive('nohy_rovina') ) return

					if ( this.isAnimationActive('striska') ) {
						await this.playAnimation('nohy_rovina', { weight: 50 })
					} else {
						await this.playAnimation('striska')
					}
				},

				'autocontour_down': async () => {
					await Promise.all([
						this.positionHandlers().thighrest_adjustment_down(),
						this.positionHandlers().backrest_adjustment_down()
					])

					if ( this.isAnimationActive('striska') ) {
						await this.positionHandlers().thighrest_adjustment_down()
					}
				},
				'autocontour_up': async () => {
					const promises = []

					promises.push( this.positionHandlers().backrest_adjustment_up() )

					if ( !this.isAnimationActive('striska') ) {
						promises.push( this.playAnimation('striska') )
					}

					if ( this.isAnimationActive('thighrest_adjustment_up') ) {
						promises.push( this.playAnimation('thighrest_adjustment_up', { reverse: true, fadeIn: false } ) )
					}

					return Promise.all(promises)
				},

				'lateral_l': async () => {
					if ( this.isAnimationActive(['lateral_l', ...trendelenburgPositions]) ) return
					if ( this.isAnimationActive('mid_to_min') ) {
						await this.positionHandlers().bed_height_adjustment_up()
					}
					if ( this.isAnimationActive('lateral_r') ) {
						await this.playAnimation('lateral_r', { reverse: true, fadeIn: false })
					} else {
						await this.playAnimation('lateral_l')
					}
				},
				'lateral_r': async () => {
					if ( this.isAnimationActive(['lateral_r', ...trendelenburgPositions]) ) return
					if ( this.isAnimationActive('mid_to_min') ) {
						await this.positionHandlers().bed_height_adjustment_up()
					}
					if ( this.isAnimationActive('lateral_l') ) {
						await this.playAnimation('lateral_l', { reverse: true, fadeIn: false })
					} else {
						await this.playAnimation('lateral_r')
					}
				},
				'lateral_reset': async () => {
					if ( this.isAnimationActive('lateral_l') ) {
						await this.playAnimation('lateral_l', { reverse: true, fadeIn: false })
					} else if ( this.isAnimationActive('lateral_r') ) {
						await this.playAnimation('lateral_r', { reverse: true, fadeIn: false })
					}
				},

				'cpr': async () => {
					this.fadeAnimations(this.$deleteFromArray(motorizedPositions, ['cpr', 'max_to_mid', 'mid_to_min', 'lateral_l', 'lateral_r']), 3)

					await this.positionHandlers().lateral_reset()

					if ( this.isAnimationActive(minHeightPositions) ) {
						this.fadeAnimations(this.$deleteFromArray(minHeightPositions, 'mid_to_min'), 0)
						this.fastForwardAnimation('mid_to_min')

						return
					}

					if ( this.isAnimationActive(mobilizationPositions) || this.isAnimationActive('max_to_mid') ) {
						this.positionHandlers().bed_height_adjustment_down()
						return
					}

					if ( !this.isAnimationActive('mid_to_min') ) {
						this.animateSubsequentActions([ 'max_to_mid', 'mid_to_min' ])
					}
				},

				'trendelenburg': async (resetOthers = true) => {
					if (resetOthers) {
						await Promise.all([
							this.positionHandlers().thighrest_adjustment_down(),
							this.positionHandlers().backrest_adjustment_down(),
							this.positionHandlers().lateral_reset()
						])
					}

					if ( this.isAnimationActive(trendelenburgPositions) ) return

					if ( this.isAnimationActive(reverseTrendelenburgPositions) ) {
						this.fadeAnimations( this.$deleteFromArray(reverseTrendelenburgPositions, 'trendelenburg_to_reverse_trendelenburg'))
						await this.playAnimation('trendelenburg_to_reverse_trendelenburg', { reverse: true, speed: 0.4 })
						await this.fastForwardAnimation('trendelenburg')
						this.fadeAnimations( this.$deleteFromArray(motorizedPositions, ['trendelenburg']), 1 )
						return
					}

					if ( this.isAnimationActive('max_to_mid') ) {
						this.fadeAnimations(['max_to_mid'], 0)
						this.playAnimation('mid_to_trendelenburg', { fadeIn: false })
						return
					}

					if ( this.isAnimationActive(minHeightPositions) ) {
						this.fadeAnimations(minHeightPositions, 0)
						this.playAnimation('min_to_trendelenburg', { fadeIn: false })
						return
					}

					if (resetOthers) {
						this.fadeAnimations( this.$deleteFromArray(motorizedPositions, ['trendelenburg']), 2 )
					}
					this.playAnimation('trendelenburg')
				},

				'reversetrendelenburg': async (resetOthers = true) => {
					if (resetOthers) {
						await Promise.all([
							this.positionHandlers().thighrest_adjustment_down(),
							this.positionHandlers().backrest_adjustment_down(),
							this.positionHandlers().lateral_reset()
						])
					}

					if ( this.isAnimationActive(reverseTrendelenburgPositions) ) return

					if ( this.isAnimationActive(trendelenburgPositions) ) {
						this.fadeAnimations(trendelenburgPositions)
						await this.playAnimation('trendelenburg_to_reverse_trendelenburg', { speed: 0.4 })
						await this.fastForwardAnimation('reversetrendelenburg')
						this.fadeAnimations( this.$deleteFromArray(motorizedPositions, ['reversetrendelenburg']), 1 )
						return
					}

					if ( this.isAnimationActive('max_to_mid') ) {
						this.fadeAnimations(['max_to_mid'], 0)
						await this.playAnimation('mid_to_reversetrendelenburg', { fadeIn: false })
						this.fadeAnimations('mid_to_reversetrendelenburg', 0.2)
						await this.fastForwardAnimation('reversetrendelenburg')
						return
					}

					if ( this.isAnimationActive(minHeightPositions) ) {
						this.fadeAnimations(minHeightPositions, 0)
						this.fadeAnimations(mobilizationPositions, 1)
						await this.playAnimation('min_to_reversetrendelenburg', { fadeIn: false })
						await this.fastForwardAnimation('reversetrendelenburg')
						await this.fadeAnimations('min_to_reversetrendelenburg', 0.2)
						return
					}

					let start = 0
					if ( this.isAnimationActive('cardiac_chair') ) start = 2

					if (resetOthers) {
						this.fadeAnimations( this.$deleteFromArray(motorizedPositions, 'reversetrendelenburg'), 2 )
					}
					await this.playAnimation('reversetrendelenburg', { start })
				},

				'mobilization_position': async () => {
					await Promise.all([
						this.reverseIfActive('striska'),
						this.positionHandlers().thighrest_adjustment_down(),
						this.positionHandlers().lateral_reset()
					])

					// assure mid height
					if ( this.isAnimationActive(minHeightPositions) ) {
						this.positionHandlers().bed_height_adjustment_up()
					} else if ( !this.isAnimationActive([ 'max_to_mid', ...trendelenburgPositions, ...reverseTrendelenburgPositions ]) ) {
						this.positionHandlers().bed_height_adjustment_down()
					}

					// from trendelenburg
					if ( this.isAnimationActive(trendelenburgPositions) ) {
						if ( this.isAnimationActive('trendelenburg_backrest_adjustment_70') ) {
							this.playAnimation('backrest_adjustment_up', { start: 2.7 })
						}
						this.fadeAnimations(this.$deleteFromArray(trendelenburgPositions, 'mid_to_trendelenburg'), 1)

						await this.playAnimation('mid_to_trendelenburg', { reverse: true, fadeIn: false })

						this.fadeAnimations('mid_to_trendelenburg', 0)
						this.fastForwardAnimation('max_to_mid')
					}

					// from reverse trendelenburg
					if ( this.isAnimationActive(reverseTrendelenburgPositions) ) {
						if ( this.isAnimationActive('reversetrendelenburg_backrest_adjustment_70') ) {
							this.playAnimation('backrest_adjustment_up', { start: 2.7 })
						}
						this.fadeAnimations(this.$deleteFromArray(reverseTrendelenburgPositions, 'mid_to_reversetrendelenburg'), 1)

						await this.playAnimation('mid_to_reversetrendelenburg', { reverse: true, fadeIn: false })

						this.fadeAnimations('mid_to_reversetrendelenburg', 0)
						this.fastForwardAnimation('max_to_mid')
					}

					if ( !this.isAnimationActive([ 'backrest_adjustment_up', 'reversetrendelenburg_backrest_adjustment_70' ]) ) {
						this.positionHandlers().backrest_adjustment_up()
					}
				},

				'cardiac_chair': async () => {
					this.positionHandlers().lateral_reset()

					if ( !this.isAnimationActive(reverseTrendelenburgPositions) ) {
						await this.positionHandlers().reversetrendelenburg(false)
					}

					if ( !this.isAnimationActive([ 'backrest_adjustment_up', 'reversetrendelenburg_backrest_adjustment_70' ]) ) {
						this.positionHandlers().backrest_adjustment_up()
					}

					if ( !this.isAnimationActive('striska') ) {
						this.playAnimation('striska')
					}

					this.fadeAnimations('nohy_rovina', 2)
				}
			}
		},

		async playAnimation(name, { reverse = false, blendFrom = '', speed = 1, fadeIn = true, start = 0, weight = 1 } = {}) {
			console.log(`${reverse ? 'Reversing' : 'Playing'} animation: ${name}${blendFrom ? ', blending from ' + blendFrom : ''}`)

			const action = this.modelActions[name]

			if (!action) return

			const timeScaleSign = reverse ? -1 : 1
			const duration = action.getClip().duration / speed

			// start the rendering loop
			this.modelViewer.play()

			if (!reverse) {
				action.reset()
				action.time = start
			} else {
				action.time = action.getClip().duration - start
			}

			if (blendFrom) {
				action.crossFadeFrom(this.modelActions[blendFrom], duration)
			} else {
				action.setEffectiveTimeScale(speed * timeScaleSign)
				action.setEffectiveWeight(weight)
				action.enabled = true
				action.paused = false
			}

			if (fadeIn) action.fadeIn(1)
			action.play()

			this.lastPlayedName = name
			this.lastPlayedAction = action
			this.animationsInProgress++

			if (blendFrom) {
				await new Promise(res => setTimeout(() => {
					this.modelActions[blendFrom].time = 0
					res()
				}, duration * 1000))
			} else {
				await this.onAnimationFinished(name)
			}

			// save resources by pausing the rendering loop when no animations are playing
			// if (this.animationsInProgress === 1) this.modelViewer.pause()
			this.animationsInProgress--
			console.log(`${reverse ? 'Reversing' : 'Animation'} ${name} finished`)

			if (this.$store.state.debug) this.activeAnimationsDebug()

			return
		},

		onAnimationFinished(name) {
			const action = this.modelActions[name]
			return new Promise(resolve => {
				// set up listener when animation finishes
				this.modelViewer.animationMixer.addEventListener('finished', action.finishedCallback = e => {
					const finishedTrackName = e.action.getClip().name

					if (finishedTrackName === this.animationsList[name].track) {
						this.modelViewer.animationMixer.removeEventListener('finished', action.finishedCallback)
						resolve()
					}
				})
			})
		},

		fastForwardAnimation(name) {
			return name in this.modelActions && this.playAnimation(name, { start: this.modelActions[name].getClip().duration, fadeIn: false })
		},

		reverseIfActive(animationNames) {
			const _animationNames = typeof animationNames === 'string' ? [animationNames] : animationNames
			const promises = []

			_animationNames.forEach(name => {
				if ( name in this.modelActions && this.isAnimationActive(name) ) {
					promises.push( this.playAnimation(name, { reverse: true, fadeIn: false }) )
				}
			})

			return Promise.all(promises)
		},

		async fadeAnimations(names, time = 1) {
			const _names = typeof names === 'string' ? [names] : names
			this.modelViewer.play()

			const promises = []
			_names.forEach(name => {
				console.log('Fading action: ' + name)
				const action = this.modelActions[name]

				if (!action) return

				const duration = time === 'auto' ? action.getClip().duration : time

				promises.push(new Promise(resolve => {
					// if (action.time === 0 && action.getEffectiveWeight() > 0) {
					// 	resolve()
					// 	return
					// }
					action.fadeOut(duration)
					this.animationsInProgress++

					setTimeout(() => {
						action.time = 0
						action.timeScale = 1
						this.animationsInProgress--
						resolve()
					}, duration * 1000)
				}))
			})

			return Promise.all(promises).then(() => {
				this.lastPlayedName = null
				this.lastPlayedAction = null
				this.lastRequestedAnimation = null

				if (this.$store.state.debug) this.activeAnimationsDebug()
				// save resources by pausing the rendering loop when no animations are playing
				// if (this.animationsInProgress === 0) this.modelViewer.pause()
			})
		},

		async resetModel(exclude) {
			console.log('Resetting all animations on model')

			this.activePreset = null
			this.activePresetAction = null

			const toFade = exclude ? this.$deleteFromArray(Object.keys(this.modelActions), exclude) : Object.keys(this.modelActions)

			await this.fadeAnimations(toFade, 1)
		},

		setModelNodeVisibility(nodeName, visibility) {
			const node = this.modelViewer.model.getNodeByName(nodeName)
			const object3d = this.modelViewer.scene.getChildByName(nodeName)

			this.modelViewer.play()

			if (node?.mesh) node.setMeshVisibility(visibility)
			if (object3d) object3d.visible = visibility

			setTimeout(() => {
				if (this.animationsInProgress === 0) this.modelViewer.pause()
			}, 100)
		},

		async setModelNodeTexture(nodeName, textureName) {
			// const node = this.modelViewer.model.primitives.find(item => item.name === nodeName)
			const node = this.modelViewer.model.getNodeByName(nodeName)
			const baseColorTexture = node.getActiveMaterial()?.pbrMetallicRoughness.baseColorTexture

			const texturePath = `/models/${this.$store.state.activeProduct}/textures/${textureName}`
			const newTexture = textureName ? this.extraTextures[textureName] || await this.modelViewer.createTexture(texturePath) : null

			if (baseColorTexture) {
				baseColorTexture.setTexture(newTexture)
			}

			this.extraTextures[textureName] = newTexture
		},

		updateProgress(e) {
			this.$store.commit('setModelLoadingProgress', e.detail.totalProgress)
		},

		hideInteractionPrompt() {
			this.modelPrompt = 'none'
		},

		setCameraPosition(position) {
			this.modelViewer.cameraOrbit = position.orbit === 'initial' ? this.initialCameraOrbit : position.orbit
			this.modelViewer.cameraTarget = position.target === 'initial' ? '0 0 0' : position.target
			this.modelViewer.fieldOfView = position.fov || 'auto'
		},

		resetCameraPosition() {
			this.setCameraPosition({
				orbit: this.initialCameraOrbit,
				target: 'auto auto auto',
				fov: 'auto'
			})
		},

		onHotspotClick(hotspot) {
			const explanations = hotspot.dataset.explanations?.split('.')
			if (explanations) {
				const explanation = explanations[0]
				const subExplanation = explanations[1] || null
				const explanationItem = this.productConfig.explanations[explanation]
				const subExplanationItem = this.productConfig.explanations[subExplanation]
				this.$store.commit('navigation/setActiveSecondary', 'key_features.' + explanation)
				this.$store.commit('setExplanationPaneContent', explanationItem)
				this.$store.commit('setExplanationPaneSub', subExplanation)
				this.$store.commit('setExplanationPaneOpen', true)

				if (subExplanationItem?.camera) {
					this.setCameraPosition(subExplanationItem.camera)
				}
			}
		},

		onCameraChange() {
			this.cameraOrbit = this.modelViewer?.getCameraOrbit() || ''
			this.cameraTarget = this.modelViewer?.getCameraTarget() || ''
		},

		activeAnimationsDebug() {
			const activeActions = Object.keys(this.modelActions).filter(it => this.isAnimationActive(it))
			this.$store.commit('setActiveAnimations', activeActions)
		},

		async animateActionWeight(actionName, startWeight = 0, targetWeight, duration) {
			const goingUp = startWeight < targetWeight
			let time = goingUp ? 0 : duration
			let weight = startWeight
			const fps = 60

			this.animationsInProgress++

			return new Promise(resolve => {
				const handler = setInterval(() => {
					if (startWeight < targetWeight) time += 1 / fps
					else time -= 1 / fps

					weight = easeInOutCubic(time * 100 / duration, time, goingUp ? startWeight : targetWeight, goingUp ? targetWeight : startWeight, duration)

					if (goingUp ? weight > targetWeight : weight < targetWeight) {
						clearInterval(handler)
						this.modelActions[actionName].setEffectiveWeight(targetWeight)
						this.animationsInProgress--
						resolve()
					}
					this.modelActions[actionName].setEffectiveWeight(weight)
				}, 1000 / fps)
			})
		}
	},
	watch: {
		activeSecondaryNavigation(val) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// disable hotspots not related to active feature
				this.hotspots.forEach(hotspot => {
					const navItemName = val.split('.')[1]
					const hotspotExplanations = hotspot.dataset.explanations?.split('.')
					hotspot.classList.remove('active')
					if (hotspotExplanations[0] === navItemName) {
						if (hotspotExplanations.length === 1) {
							hotspot.classList.add('active')
						}
						hotspot.removeAttribute('disabled')
					} else {
						hotspot.setAttribute('disabled', '')
					}
				})
			}
		},
		activeSubExplanation(name) {
			if (this.activePrimaryNavigation === 'keyFeatures') {
				// display hotspot as active when related feature + subExplanation is selected
				this.hotspots.forEach(hotspot => {
					const hotspotExplanation = hotspot.dataset.explanations?.split('.')[1]
					if (hotspotExplanation === name) {
						hotspot.classList.add('active')
					} else {
						hotspot.classList.remove('active')
					}
				})
			}
		},
		explanationPaneOpen(open) {
			if (!open) {
				this.$nextTick(() => {
					this.resetCameraPosition()
				})
			}
		}
	},
}
</script>

<style lang="scss" scoped>
model-viewer {
	width: 100%;
	height: 100%;
	--poster-color: transparent;
	--min-hotspot-opacity: 0;

	&::part(default-progress-bar) {
		top: 30px;
		background-color: $clrRed;
	}
}

::v-deep(.Hotspot) {
	display: none;
	position: relative;
	width: 26px;
	height: 26px;
	background-color: transparentize($color: $clrWhite, $amount: 0.2);
	border-radius: 100px;
	box-shadow: 0 0 0 4px transparentize($color: $clrWhite, $amount: 0.5);
	transition: all 300ms ease 200ms;
	&.active,
	&:hover,
	&:disabled,
	&:not([data-visible]) {
		background-color: transparent;
		box-shadow: 0 0 0 0 transparent;
		transition: all 300ms ease;
	}
	&:disabled {
		pointer-events: none;
	}
	.hotspots-visible & {
		display: block;
	}

	&::before {
		content: "";
		position: absolute;
		width: 17px;
		height: 17px;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		border-radius: 100px;
		border: 1px solid $clrWhite;
		background-color: $clrRed;
		background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M128 29.799v196.403M226.202 128H29.799' stroke-linecap='round' stroke-miterlimit='10' stroke-width='51.7px' fill='none' stroke='%23FFFFFF'/%3E%3C/svg%3E%0A");
		background-size: 10px;
		background-repeat: no-repeat;
		background-position: center;
		transition: all 300ms ease 200ms;
	}
	&.active::before,
	&:hover::before,
	&:disabled::before,
	&:not([data-visible])::before {
		transform: translate(-50%, -50%) scale(0.6);
		opacity: 0;
		transition: all 300ms ease;
	}

	.HotspotAnnotation {
		position: absolute;
		left: 50%;
		bottom: 24px;
		padding: 0.2em 0.75em;
		transform: translate(-50%, 30%);
		opacity: 0;
		font-size: 12px;
		font-weight: 500;
		white-space: nowrap;
		border-radius: 100px;
		background-color: $clrWhite;
		transition: all 300ms ease;
		pointer-events: none;
		z-index: 100;

		&::before {
			content: "";
			position: absolute;
			width: 0;
			height: 0;
			bottom: -6px;
			left: calc(50% - 6px);
			border-style: solid;
			border-width: 6px 6px 0 6px;
			border-color: #ffffff transparent transparent transparent;
		}
	}
	&:hover, &.active {
		.HotspotAnnotation {
			opacity: 1;
			transform: translate(-50%, 0);
			pointer-events: all;
			color: $clrRed;
		}
	}
}

.debug-info {
	position: absolute;
	top: 50px;
	left: 340px;
	width: calc(100vw - 360px);
	font-size: 10px;
	display: flex;
	justify-content: space-between;
}
</style>
